import Button from "@mui/material/Button";
import { ComponentProps, JSEvalContext } from "../types";
import { CSSProperties, useMemo } from "react";
import { Icon, Tooltip } from "@mui/material";
import DashboardPageContext from "../DashboardPageContext";
import { useContext, useState } from "react";
import ExecutionTypeContext, { Mode } from "../ExecutionTypeContext";
import DashboardEnvContext from "../DashboardEnvContext";
import { evalJs } from "../js-utils";
import { LoadingButton } from "@mui/lab";
import PagetActionsContext from "../PagetActionsContext";

function Component({ component, evaluteScript }: ComponentProps) {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const dashboard = useContext(DashboardEnvContext);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const pageContext = useContext(DashboardPageContext);
	const exeMode = useContext(ExecutionTypeContext);
	const [buildingError, setBuildingError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const pageActions = useContext(PagetActionsContext);

	const style = useMemo(() => {
		const style = {} as CSSProperties;
		if (exeMode?.mode === Mode.DESIGN && component.id === exeMode?.activeComponent?.id) style.border = "1px dashed red";
		if (component.properties["width"]) style.width = component.properties["width"];
		return style;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [component, pageContext.context, exeMode]);

	const args = useMemo(() => {
		const args = {} as { [key: string]: unknown };
		args["variant"] = (component.properties?.["variant"] || "text") as "text" | "elevation" | "outlined";
		args["color"] = (component.properties["color"] || "inherit") as "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
		if (component.properties["icon"]) args["startIcon"] = <Icon>{component.properties["icon"]}</Icon>;
		if (component.properties["submitAction"]) {
			args["onClick"] = async () => {
				let actionPayload = undefined as unknown;
				if (component.properties["submitAction_Payload"]) {
					actionPayload = evaluteScript("const payload = " + component.properties["submitAction_Payload"] + "; payload");
				}
				if (dashboard) {
					setIsLoading(true);
					pageActions.executePageAction(
						component.properties["submitAction"],
						{
							row: undefined,
							actionPayload: actionPayload,
						},
						() => setIsLoading(false)
					);
				}
			};
		}

		if (exeMode?.mode === Mode.DESIGN && exeMode.locatorActivated === true) {
			args["onClick"] = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
				if (exeMode.updateActiveComponent) exeMode.updateActiveComponent(component);
				event.stopPropagation();
			};
		}
		if (component.properties["disabled"])
			args["disabled"] = evalJs(
				{
					pageContext,
					dashboard,
					exeMode,
					logError: (error) => {
						setBuildingError(error);
					},
				} as JSEvalContext,
				component.properties["disabled"]
			);
		return args;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [component, pageContext.context, exeMode]);
	const componentStyles = useMemo(() => {
		try {
			if (component.styles) {
				const styles = typeof component.styles === "string" ? evaluteScript(component.styles) : component.styles;
				if (styles != undefined) return styles as CSSProperties;
			}
		} catch (error) {
			console.error(error);
		}
		return {} as CSSProperties;
	}, [component]);
	return (
		<>
			{buildingError === null && (
				<Tooltip title={component.properties["tooltip"]}>
					<span>
						{(!component.properties["loadingButton"] || component.properties["loadingButton"] === "False") && (
							<Button key={component.id} {...args} sx={component.styles ? { ...style, ...componentStyles } : style}>
								{component.properties["label"]}
							</Button>
						)}
						{component.properties["loadingButton"] && component.properties["loadingButton"] === "True" && (
							<LoadingButton loadingPosition="start" loading={isLoading} key={component.id} {...args} sx={component.styles ? { ...style, ...componentStyles } : style}>
								{component.properties["label"]}
							</LoadingButton>
						)}
					</span>
				</Tooltip>
			)}
			{buildingError !== null && (
				<Tooltip title={buildingError} componentsProps={{ tooltip: { sx: { backgroundColor: "red" } } }}>
					<Icon sx={{ color: "red" }}>error</Icon>
				</Tooltip>
			)}
		</>
	);
}

export default Component;
